/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import Modal from 'react-modal'
import { withRouter } from 'react-router'
import { isEmpty } from 'lodash'

import { getCartItems, getTotal } from '../../selectors/cart'
import { getKioskPayment, getOrderInProgress } from '../../selectors/order'
import { KIOSK_PAYMENT_TYPES } from '../../utils/paymentTypes'

import { toggleAppState } from '../../actions/appState'
import {
  startOfflineCardOrder,
  startOnlineCardOrder,
  storeOrderUuidAndOrderNumber,
  closeNoOpTender,
  addPaymentToOrderInProgress,
  restoreQRPaymentandAuthorizationFailedStatus,
} from '../../actions/order'
import { resetSafFullResponse } from '../../actions/peripheral'

// import analytics from '../utils/analytics'
import { formatPrice } from '../utils/formatters'
import { centsToDollars } from '../../utils/formatters'
import { getDeviceSerial } from '../../utils/deviceData'
import PeripheralBridge from '../../utils/peripheralBridge'
import { nextPosOrderNumber, invoiceNumber, orderUuidFromOrderNumber } from '../../utils/orderNumbers'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../../constants'
import OfflineModal from '../OfflineModal'

import { fakeCreditCardResponse, showFakeCreditCard } from '../../utils/fakeCreditCardResponse'
import { KEY_CODES } from '../utils/contants'

import Button from '../components/Button'
import BackButton from '../components/BackButton'
import ConfirmModalV2 from '../../components/ConfirmModalV2'
import { ReactComponent as IconCardReader } from '../assets/icons/card-reader.svg'
import { ReactComponent as IconCard } from '../assets/icons/card.svg'
import { ReactComponent as IconArrow } from '../assets/icons/arrow.svg'

import useIdleMonitor from '../hooks/useIdleMonitor'
import useRemoteOrderTotal from '../../hooks/useRemoteOrderTotal'
import { isOfflineApproval } from '../../utils/offlineReplayStatuses'
import { cardReaderApproved } from '../../utils/orderUtils'
import { getTransactionBrandingConfig } from '../../utils/buildSettings'

import { getTipsEnabled, makeGetCurrentMenu } from '../../selectors/menus'
import { getTipOnCardReader } from '../../selectors/peripheral'
import { getKioskOfflineEnabled } from '../utils/config'
import ModalMessage from '../AttractLoop/ModalMessage'

const cancelModalTexts = {
  Ticket: 'Canceling this transaction\nwill return all funds to your\ntickets.',
  'Gift card': 'Cancel Transaction? The gift\ncard will not be charged.',
  'QR code': 'Cancel Transaction? The\nmobile account will not be\ncharged.',
}

export const Payment = ({
  clearSafResults,
  getNextOrderNumber,
  history,
  kioskLineItems,
  onSuccess,
  onOfflineSuccess,
  placeFreeOrder,
  kioskPayment,
  tipAmountInCents,
  charityAmountInCents,
  totalInCents,
  tipSuggestions,
  tipsEnabledOnCardReader,
  tenderAmountInCents,
  completeTicketOrder,
  uuid,
  paymentWasSuccessful,
  modalSubtext,
  scanScreenType,
  orderInProgress,
}) => {
  const [orderNumber] = useState(getNextOrderNumber)
  const [orderUuid, setOrderUuid] = useState(uuid)
  const [showPaymentCancelledByUserWarning, setShowPaymentCancelledByUserWarning] = useState(false)
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, loading, success, failed }] = useRemoteOrderTotal()
  const backButtonRef = useRef()
  const kioskOfflineEnabled = getKioskOfflineEnabled({ fromAttractLoop: false })

  const offlineTotal = useSelector(getTotal)
  const total = kioskOfflineEnabled ? offlineTotal : 0

  const totalOrderPrice =
    tenderAmountInCents ?? (remoteOrderTotal?.totalAmountInCents ?? total ?? 0) + tipAmountInCents + charityAmountInCents
  const threshHoldExceeded =
    SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && totalOrderPrice >= SIGNATURE_THRESHOLD_AMOUNT)
  const signatureRequired = !isEmpty(tipSuggestions) || threshHoldExceeded

  const noPaymentRequired = totalOrderPrice === 0
  const formattedTotal = ((totalOrderPrice || 0) / 100).toFixed(2)
  const doneLoading = tenderAmountInCents || (success && !!orderUuid) || failed
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [loadingModal, setLoadingModal] = useState(null)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!tenderAmountInCents) {
      loadRemoteOrderTotal()
    }
    clearSafResults()
  }, [])

  useEffect(() => {
    if (success) {
      setOrderUuid(remoteOrderTotal?.uuid)
      return
    }

    //if totalling failed and kos is enabled then we need to handle the order as offline
    if (failed && kioskOfflineEnabled) {
      setOrderUuid(orderUuidFromOrderNumber(orderNumber))
    }
  }, [success, kioskOfflineEnabled, failed])

  const getCardReaderReady = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (bridgeInstance)
      bridgeInstance.call('executeSaleRequest', {
        uuid: orderUuid,
        orderNumber,
        hRef: invoiceNumber(orderNumber),
        // this amount is including tip and we are missing "tipAmount"
        amount: formattedTotal,
        deviceSerial: getDeviceSerial(),
        tipEnabled: tipsEnabledOnCardReader,
        ...getTransactionBrandingConfig(),
      })
  }

  const { idleMonitor } = useIdleMonitor()

  useEffect(() => {
    // analytics.paymentEvent(cart)
    document.title = 'Order payment'

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance || noPaymentRequired) return

    if ((success && orderUuid) || tenderAmountInCents || (failed && kioskOfflineEnabled && orderUuid)) {
      bridgeInstance.registerHandler('handleResult', (data) => {
        if (cardReaderApproved(data)) {
          const dataWithUuid = { ...data, uuid: orderUuid, orderNumber }

          // TODO (JEFF): Discuss whether to remove onOfflineSuccess from kiosk mode and what possible ramifications are.
          if (tenderAmountInCents) {
            setLoadingModal('Loading')

            completeTicketOrder(
              dataWithUuid,
              orderNumber,
              tipAmountInCents,
              tenderAmountInCents,
              orderUuid,
              charityAmountInCents,
              isOfflineApproval({ cardReaderData: data }),
            )
            return
          }
          isOfflineApproval({ cardReaderData: data })
            ? onOfflineSuccess(dataWithUuid, kioskLineItems, orderNumber)
            : onSuccess(dataWithUuid, kioskLineItems, orderNumber)
          redirectToNextStep()
        } else {
          setShowPaymentCancelledByUserWarning(true)
        }
      })

      getCardReaderReady()
    }

    return () => {
      bridgeInstance.call('cancelTransactionAttempt')
    }
  }, [formattedTotal, success, orderUuid, failed, kioskOfflineEnabled])

  useEffect(() => {
    backButtonRef.current && backButtonRef.current.focus()

    idleMonitor.addEventListeners()
    return () => {
      idleMonitor.removeEventListeners()
    }
  }, [])

  useEffect(() => {
    if (noPaymentRequired && (success || kioskOfflineEnabled) && orderUuid) {
      placeFreeOrder(orderUuid)
    }
  }, [success, orderUuid])

  const redirectToNextStep = () => {
    history.push(`/kiosk/order/summary/${orderUuid}`)
  }

  const onFakeCreditCardSwipe = () => {
    if (tenderAmountInCents) {
      completeTicketOrder(
        fakeCreditCardResponse(orderUuid, formattedTotal),
        orderNumber,
        tipAmountInCents,
        tenderAmountInCents,
        orderUuid,
        charityAmountInCents,
      )
      return
    } else {
      onSuccess({ ...fakeCreditCardResponse(orderUuid, formattedTotal), orderNumber }, kioskLineItems, orderNumber)
      redirectToNextStep()
    }
  }

  const retryPaymentFlow = () => {
    setShowPaymentCancelledByUserWarning(false)
    getCardReaderReady()
  }

  const goBack = () => {
    if (scanScreenType) return
    const bridgeInstance = PeripheralBridge.getBridge()
    if (bridgeInstance) bridgeInstance.call('cancelTransactionAttempt')
    if (
      kioskPayment === KIOSK_PAYMENT_TYPES.creditCard &&
      (((SIGNATURE_THRESHOLD_AMOUNT === 0 || SIGNATURE_THRESHOLD_AMOUNT) && signatureRequired) || !isEmpty(tipSuggestions))
    ) {
      history.push({ pathname: '/kiosk/signature', state: { showCharityFirst: charityAmountInCents > 0 } })
    } else {
      history.push('/kiosk/order/new')
    }
  }

  const handleKeyDown = (e) => {
    if (scanScreenType) return
    if (e.keyCode === KEY_CODES.UP) {
      goBack()
    }
  }

  const PaymentBackBtn = () => {
    return (
      <BackButton
        handleKeyDown={handleKeyDown}
        refProp={backButtonRef}
        className="payment__back"
        withIcon={!scanScreenType}
        onClick={goBack}
      >
        {loading ? '-' : formatPrice(totalOrderPrice)}
      </BackButton>
    )
  }

  let modal
  if (showCancelModal) {
    modal = (
      <ConfirmModalV2
        onButtonOneClick={() => setShowCancelModal(false)}
        onButtonTwoClick={() => {
          /**
           * if order has previousAuthorizationFailedStatus then that it's a failed attempt to pay with QR code
           * we set the order again to defective so it's in sync with Stadium
           */
          if (orderInProgress?.previousAuthorizationFailedStatus) {
            dispatch(restoreQRPaymentandAuthorizationFailedStatus(orderUuid))
          }
          history.push('/kiosk/order/new')
        }}
        headerText={''}
        subtext={modalSubtext}
        buttonOneText={'NO, NEVER MIND '}
        buttonTwoText={'YES, CANCEL'}
        isKiosk={true}
      />
    )
  }

  let content
  const subHeaderText = paymentWasSuccessful
    ? `${scanScreenType} scanned! Pay the remaining\nbalance of ${centsToDollars(tenderAmountInCents)} with card \n\n`
    : 'All major cards accepted\nFollow instructions on card reader'

  // for balance due scenarios (scanned tenders), success is set to false
  const hasBalance = !failed && +tenderAmountInCents > 0
  if ((doneLoading && (success || hasBalance)) || (failed && kioskOfflineEnabled)) {
    content = (
      <>
        <PaymentBackBtn />
        <div className="payment__header">Pay with card</div>
        <div className="payment__sub-header">{subHeaderText}</div>
        <div className="payment__methods -top" />
        <div className="payment__card-wrapper">
          <IconCardReader className="payment__card-reader-icon" />
          <IconArrow className="payment__arrow-icon" />
          <IconCard className="payment__card-icon" />
        </div>
        <div className="payment__methods">
          {scanScreenType ? (
            <Button
              className="btn -cancel"
              onClick={() => {
                setShowCancelModal(true)
              }}
              title="Cancel Transaction"
            />
          ) : (
            <div>Card Reader is Ready</div>
          )}
        </div>
        {modal}
        {showFakeCreditCard() && (
          <div className="fake-credit-card">
            <Button className="btn -primary" onClick={onFakeCreditCardSwipe} title="Pretend Credit Card was swiped" />
          </div>
        )}
      </>
    )
  } else if (failed) {
    content = (
      <>
        <PaymentBackBtn />
        <div className="payment__header">Totaling Failed, please back out and try again</div>
      </>
    )
  } else if (noPaymentRequired && doneLoading) {
    content = (
      <>
        <div className="payment__content">
          <PaymentBackBtn />
          <div className="payment__header">No Charge!</div>
          <div className="payment__sub-header">Submitting your order...</div>
        </div>
      </>
    )
  }

  if (loading) {
    content = (
      <>
        <div className="payment__content-loading">
          <PaymentBackBtn />
          <div className="payment__header">Loading...</div>
        </div>
      </>
    )
  }

  return (
    <div className="payment">
      {content}
      {!kioskOfflineEnabled && <OfflineModal />}
      <ModalMessage state={loadingModal} />
      <Modal className="modal" isOpen={showPaymentCancelledByUserWarning} overlayClassName="overlay">
        <div className="modal__copy">Would you like to try another card?</div>
        <div className="modal__actions">
          <Button className="btn -outline" onClick={() => history.push('/kiosk/order/new')} title="No" />
          <Button className="btn -primary" onClick={retryPaymentFlow} title="Yes" />
        </div>
      </Modal>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const kioskPayment = getKioskPayment(state)
  const tipAmountInCents = ownProps?.history.location?.state?.tipAmountInCents ?? 0
  const charityAmountInCents = ownProps?.history.location?.state?.charityAmountInCents ?? 0
  const tenderAmountInCents = ownProps?.history.location?.state?.tenderAmountInCents
  const paymentWasSuccessful = ownProps?.history.location?.state?.paymentWasSuccessful
  const getMenu = makeGetCurrentMenu()
  const menu = getMenu(state)
  const tipSuggestions = menu?.tipSuggestions
  const orderInProgress = getOrderInProgress(state)
  const uuid = orderInProgress?.uuid
  const scanScreenType = ownProps?.history.location?.state?.scanScreenType
  const modalSubtext = cancelModalTexts[scanScreenType]
  const tipsEnabledOnCardReader = getTipsEnabled(state) && getTipOnCardReader(state)

  return {
    getNextOrderNumber: nextPosOrderNumber,
    kioskLineItems: getCartItems(state),
    kioskPayment,
    tipAmountInCents,
    charityAmountInCents,
    tipSuggestions,
    tipsEnabledOnCardReader,
    tenderAmountInCents,
    uuid,
    paymentWasSuccessful,
    modalSubtext,
    scanScreenType,
    orderInProgress,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const receiptSignature = props?.history?.location?.state?.receiptSignature ?? 0
  const tipAmountInCents = props?.history.location?.state?.tipAmountInCents ?? 0
  const charityAmountInCents = props?.history.location?.state?.charityAmountInCents ?? 0
  const push = props?.history?.push ?? (() => {})

  return {
    onOfflineSuccess: (cardReaderData, lineItems, orderNumber) => {
      dispatch(
        startOfflineCardOrder({
          cardReaderData,
          signature: receiptSignature,
          lineItems,
          tipAmountInCents,
          charityAmountInCents,
          isKiosk: true,
          orderNumber,
        }),
      )
    },
    onSuccess: (cardReaderData, lineItems, orderNumber) => {
      dispatch(
        startOnlineCardOrder({
          cardReaderData,
          signature: receiptSignature,
          lineItems,
          tipAmountInCents,
          charityAmountInCents,
          isKiosk: true,
          orderNumber,
        }),
      )
    },
    toggleAppState: (data) => {
      dispatch(toggleAppState({ data }))
    },
    storeOrderUuidAndOrderNumber: (orderUuid, orderNumber) => {
      dispatch(storeOrderUuidAndOrderNumber({ orderUuid, orderNumber }))
    },
    placeFreeOrder: (orderUuid) => {
      dispatch(closeNoOpTender({ orderUuid }))
      props.history.push(`/kiosk/order/summary/${orderUuid}`)
    },
    completeTicketOrder: (
      cardReaderData,
      orderNumber,
      tipAmountInCents,
      tenderAmountInCents,
      orderUuid,
      charityAmountInCents,
      isApprovedOffline,
    ) => {
      dispatch(
        addPaymentToOrderInProgress({
          cardReaderData,
          orderNumber,
          tipAmountInCents,
          charityAmountInCents,
          tenderAmountInCents,
          paymentType: 'freedompay_credit',
          push,
          isApprovedOffline,
        }),
      )
    },
    clearSafResults: () => {
      // Clear the SAF results so that the next time the user goes to the card reader screen, the
      // results are not stale.
      dispatch(resetSafFullResponse())
    },
  }
}

Payment.defaultProps = {
  cart: [],
  kioskLineItems: [],
  totalOrderPrice: 0,
}

Payment.propTypes = {
  deviceId: PropTypes.string,
  getNextOrderNumber: PropTypes.func,
  kioskLineItems: PropTypes.array,
  onOfflineSuccess: PropTypes.func,
  onSuccess: PropTypes.func,
  toggleAppState: PropTypes.func,
  totalOrderPrice: PropTypes.number,
  kioskPayment: PropTypes.string,
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withRouter)(Payment)
